import React, {
  createContext,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import "./ButtonAction.module.scss";
import classNames from "classnames";

const ButtonActionContext = createContext();
export const useButtonActionContext = () => useContext(ButtonActionContext);

/**
 * @params isActive boolean
 * @params icon string? 
 * @params label string
 * @params children function({close}): ReactNode
 * @params buttonClassNames string?
 * @params modalClassNames string?
 *
 * @returns
 */
export const ButtonAction = forwardRef(({
  isActive,
  icon,
  label,
  children,
  buttonClassNames = '',
  modalClassNames,
}, ref) => {
  const [open, setOpen] = useState(false);
  const modalRef = useRef();
  const btnRef = useRef();
  // Tracks whether the upcoming focus event originates from a mouse/touch
  // press (which is immediately followed by the click's toggleModal), so
  // onFocus doesn't open the dropdown right before the click closes it again.
  const isPointerInteractionRef = useRef(false);

  // Expose an imperative `focus()` so parent flows (e.g. auto-advance to the
  // next form field) can move keyboard focus here from outside. Focusing the
  // button also opens the dropdown via the onFocus handler below.
  useImperativeHandle(ref, () => ({
    focus: () => btnRef.current?.focus(),
  }));

  /**fix: hook value doenst change in listener event */
  const stateRef = React.useRef(open);
  const setOpenModal = (isOpen) => {
    setOpen(isOpen);
    stateRef.current = isOpen;
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const toggleModal = () => {
    setOpenModal(!open);
    // Clear the pointer-interaction flag here too: if the button was already
    // focused before this click, no onFocus event fires to consume/reset it,
    // which would otherwise make the *next* keyboard/programmatic focus
    // wrongly get treated as part of a pointer click and skip opening.
    isPointerInteractionRef.current = false;
  };

  const handleClick = (e) => {
    if (
      !modalRef?.current ||
      !stateRef.current ||
      btnRef.current.contains(e.target) ||
      // this line for fixing dropdown issue, element not exists when option selected
      !document.contains(e.target)
    ) {
      return;
    }

    if (!modalRef.current.contains(e.target)) {
      closeModal();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <ButtonActionContext.Provider
      value={{
        close: closeModal,
      }}
    >
      <div className="btn-action-root">
        <button
          ref={btnRef}
          className={classNames(
            "btn-action-container",
            isActive || open ? "active" : "",
            buttonClassNames,
          )}
          onClick={toggleModal}
          onMouseDown={() => { isPointerInteractionRef.current = true; }}
          onFocus={() => {
            // A pointer press fires focus first, then click's toggleModal -
            // let toggleModal alone decide the open state for that click so
            // the dropdown doesn't open-then-immediately-close.
            if (isPointerInteractionRef.current) {
              isPointerInteractionRef.current = false;
              return;
            }
            setOpenModal(true);
          }}
        >
          {icon && <img src={icon} />}

          {label}
          <div
            className={classNames(
              "btn-action-arrow",
              open ? "lifepal-arrow-up" : "lifepal-arrow-down",
              buttonClassNames.includes('center-text') ? 'center-text-right-arrow' : ''
            )}
          ></div>
        </button>

        {open && (
          <div className={classNames("btn-action-modal",
            modalClassNames ?? '',
          )}
            ref={modalRef}
          >
            {children}
          </div>
        )}
      </div>
    </ButtonActionContext.Provider>
  );
});

ButtonAction.displayName = 'ButtonAction';
